import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useBottomNavBar } from "../contexts/BottomNavBarContext";
import { useUser } from "../contexts/UserContext";
import config from "../configs/config";
import { CreditOptionsOverlay } from "./CreditOptionsMenu";

// Import all icons from assets
import guidesIcon from "../assets/icons/guides-icon.svg";
import guidesBlueIcon from "../assets/icons/guide-blue-icon.svg";
import creditIcon from "../assets/icons/credit-icon.svg";
import creditBlueIcon from "../assets/icons/credit-blue-icon.svg";
import benefitsIcon from "../assets/icons/benefits-icon.svg";
import benefitsBlueIcon from "../assets/icons/benefits-blue-icon.svg";
import discountsIcon from "../assets/icons/discounts-icon.svg";
import discountsBlueIcon from "../assets/icons/discounts-blue-icon.svg";

interface NavItem {
  icon: string;
  activeIcon?: string;
  label: string;
  path: string;
  matchPaths?: string[];
  isImage?: boolean;
  /** Opens the credit options menu instead of navigating */
  opensCreditMenu?: boolean;
}

const navItems: NavItem[] = [
  {
    icon: guidesIcon,
    activeIcon: guidesBlueIcon,
    label: "Guides",
    path: `${config.AFFORDA_STATIC_SITE}/guides/`,
    isImage: true,
  },
  {
    icon: creditIcon,
    activeIcon: creditBlueIcon,
    label: "Credit",
    path: "/loans-landing",
    isImage: true,
    opensCreditMenu: true,
  },
  {
    icon: benefitsIcon,
    activeIcon: benefitsBlueIcon,
    label: "Benefits",
    path: "/getting-started",
    matchPaths: ["/getting-started", "/benefits-summary", "/quick-results"],
    isImage: true,
  },
  {
    icon: discountsIcon,
    activeIcon: discountsBlueIcon,
    label: "Discounts",
    path: "/discounts",
    matchPaths: ["/discounts"],
    isImage: true,
  },
];

const BottomNavBar: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isVisible } = useBottomNavBar();
  const { setSaveOnShopping } = useUser();
  const [isCreditMenuOpen, setIsCreditMenuOpen] = useState(false);

  if (!isVisible) {
    return null;
  }

  const handleNavigation = (item: NavItem) => {
    const { path } = item;

    if (item.opensCreditMenu) {
      setIsCreditMenuOpen((open) => !open);
      return;
    }
    setIsCreditMenuOpen(false);

    if (path === "/discounts") {
      setSaveOnShopping();
    }

    // Check if it's an external link (starts with http)
    if (path.startsWith("http")) {
      window.location.href = path;
    } else {
      navigate(path);
    }
  };

  return (
    <>
      <CreditOptionsOverlay
        isOpen={isCreditMenuOpen}
        onClose={() => setIsCreditMenuOpen(false)}
      />
      {/* Sits above the credit overlay so the Credit tab works as a toggle */}
      <nav className="fixed bottom-0 left-0 right-0 z-[60] h-[87px] flex justify-center py-2 px-4 sm:hidden">
        <div className="flex justify-around items-center rounded-40 border border-black bg-white w-full max-w-7xl">
          {navItems.map((item) => {
            // Benefits path can be different depending on the stage in user journey
            const isOnPath = item.matchPaths
              ? item.matchPaths.some((matchPath) =>
                  location.pathname.startsWith(matchPath),
                )
              : location.pathname === item.path;
            const isActive =
              isOnPath || (item.opensCreditMenu === true && isCreditMenuOpen);
            return (
              <button
                key={item.label}
                className="flex flex-col items-center justify-center group"
                onClick={() => handleNavigation(item)}
                aria-expanded={item.opensCreditMenu ? isCreditMenuOpen : undefined}
              >
                <div
                  className={`flex items-center justify-center transition-all duration-200 ${
                    isActive
                      ? "text-primary"
                      : "text-grey-80 group-hover:text-primary-hover"
                  }`}
                >
                  {/* Handle hover for the image separately */}
                  {item.isImage ? (
                    <img
                      src={
                        isActive && item.activeIcon ? item.activeIcon : item.icon
                      }
                      alt={item.label}
                      className={`w-7 h-7 object-contain transition-all duration-200 ${
                        isActive
                          ? "opacity-100" // Active state: full color, full opacity
                          : "opacity-70 group-hover:opacity-100" // Inactive state with hover effect
                      }`}
                    />
                  ) : (
                    <span>{item.icon}</span>
                  )}
                </div>
                <span className="text-sm font-medium transition-colors duration-200 mt-0.5 text-black">
                  {item.label}
                </span>
              </button>
            );
          })}
        </div>
      </nav>
    </>
  );
};

export default BottomNavBar;
